'use client'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import Cookies from 'js-cookie'
import { useSession } from 'next-auth/react'

import { useDeliverySupport } from './DeliverySupportContext'

const ONBOARDING_COOKIE_NAME = 'onboarding-address'

export interface OnboardingContextType {
  isOnboardingOpen: boolean
  setIsOnboardingOpen: (isOnboardingOpen: boolean) => void
}

export const OnboardingContext = createContext<OnboardingContextType>({
  isOnboardingOpen: false,
  setIsOnboardingOpen: () => {}
})

export const OnboardingContextProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const session = useSession()
  const { postalCodeAndCityContext } = useDeliverySupport()

  useEffect(() => {
    if (postalCodeAndCityContext.postalCode && postalCodeAndCityContext.city)
      return

    const cookieOnboarding = Cookies.get(ONBOARDING_COOKIE_NAME)

    if (cookieOnboarding !== 'true' && session.status === 'unauthenticated') {
      setIsOnboardingOpen(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.status])
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false)

  const setIsOnboardingOpenCallback = useCallback(
    (value: boolean) => {
      // Set the cookie to true if value is false(closed modal)
      if (!value) {
        const expires =
          postalCodeAndCityContext.postalCode && postalCodeAndCityContext.city
            ? 365
            : 7

        Cookies.set(ONBOARDING_COOKIE_NAME, 'true', { expires })
      }

      setIsOnboardingOpen(value)
    },
    [setIsOnboardingOpen, postalCodeAndCityContext]
  )

  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingOpen,
        setIsOnboardingOpen: setIsOnboardingOpenCallback
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboarding = () => useContext(OnboardingContext)
